import { useState } from "react";
import { Link } from "react-router-dom";
import { BOOKING_URL, CHERRY_URL, IMG } from "../../data/site";
import { Reveal } from "../Reveal";

const PILLARS = [
  ["Ratings", "5-star ratings from our clients."],
  ["Experience", "Seasoned hands behind every laser, peel and pigment."],
  ["Personalization", "No templates. A treatment plan written for you alone."],
  ["Best Practices", "Evidence-based protocols, medical oversight, safe for every skin tone."],
];

export const Standard = () => (
  <section id="experience" className="bg-white py-24 md:py-36" data-testid="standard-section">
    <div className="mx-auto grid max-w-[1400px] items-center gap-20 px-4 md:px-8 lg:grid-cols-2">
      <Reveal className="relative h-[520px] md:h-[640px]">
        <img src={IMG.room} alt="Serene treatment suite" className="absolute left-0 top-0 h-[78%] w-[72%] object-cover grayscale" loading="lazy" />
        <img src={IMG.serum} alt="Skincare ritual" className="absolute bottom-0 right-0 h-[58%] w-[52%] border-[10px] border-white object-cover" loading="lazy" />
      </Reveal>
      <Reveal>
        <p className="kicker">The Lux Veritae Standard</p>
        <h2 className="font-display mt-4 text-5xl font-light leading-[1.05] text-neutral-950 md:text-6xl">
          One address.<br /><em>Every</em> expertise.
        </h2>
        <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-neutral-600">
          Our clients choose us for our ratings, our experience, true personalization and best practices — and because everything they need lives under one roof. Lasers, skincare, injectables, permanent makeup and medical weight loss, coordinated into one long-term plan.
        </p>
        <dl className="mt-12 grid gap-x-10 gap-y-8 sm:grid-cols-2">
          {PILLARS.map(([t, d]) => (
            <div key={t} className="border-t border-neutral-200 pt-5" data-testid={`pillar-${t.toLowerCase().replace(/\s/g, "-")}`}>
              <dt className="font-display text-2xl text-neutral-950">{t}</dt>
              <dd className="mt-2 text-sm font-light leading-relaxed text-neutral-600">{d}</dd>
            </div>
          ))}
        </dl>
        <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="btn-line mt-12" data-testid="standard-consult-btn">Begin With a Consultation</a>
      </Reveal>
    </div>
  </section>
);

const SIGNATURE = [
  { id: "glass", tab: "Glass Skin Laser", img: IMG.portrait2, page: "glass-skin-laser-scottsdale", title: "Two lasers, one session.", text: "MultiFrax pairs 1927 nm and 1550 nm wavelengths to correct pigmentation and stimulate collagen in a single visit — a true skin reset with minimal downtime." },
  { id: "rf", tab: "RF Microneedling", img: IMG.rfMicroneedling, page: "rf-microneedling-scottsdale", title: "Tighten from within.", text: "Radiofrequency microneedling firms skin, refines texture and builds natural collagen for the face, neck, chest and beyond." },
  { id: "cosmelan", tab: "Cosmelan® Chemical Peel", img: IMG.peel, page: "cosmelan-peel-scottsdale", title: "Clarity at the source.", text: "Our most advanced depigmentation peel for melasma and stubborn discoloration, pairing in-office treatment with guided at-home care." },
  { id: "plasma", tab: "Plasma Fibroblast", img: "/brand/plasma-fibroblast.jpg", page: "plasma-fibroblast-scottsdale", title: "Lift without surgery.", text: "Plasma energy stimulates collagen and elastin to tighten delicate areas — from crow's feet to post-weight-loss skin." },
];

export const Signature = () => {
  const [active, setActive] = useState(SIGNATURE[0]);
  return (
    <section id="signature" className="bg-[hsl(var(--mist))] py-24 md:py-36" data-testid="signature-section">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8">
        <Reveal className="mx-auto mb-16 max-w-3xl text-center">
          <h2 className="font-display text-5xl font-light leading-none text-neutral-950 md:text-7xl">The Journey<br /><em>of Renewal</em></h2>
          <p className="mx-auto mt-8 max-w-xl text-sm font-light leading-relaxed text-neutral-600 md:text-base">
            Discover our signature treatments — cutting-edge technology chosen for meaningful, natural-looking results.
          </p>
        </Reveal>
        <div className="no-scrollbar mb-10 flex justify-start gap-8 overflow-x-auto border-b border-neutral-300 md:justify-center md:gap-14">
          {SIGNATURE.map((s) => (
            <button key={s.id} onClick={() => setActive(s)} data-testid={`signature-tab-${s.id}`} className={`shrink-0 border-b pb-4 text-[11px] uppercase tracking-[0.25em] transition-colors ${active.id === s.id ? "border-neutral-950 text-neutral-950" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>
              {s.tab}
            </button>
          ))}
        </div>
        <div key={active.id} className="rise grid items-stretch bg-white lg:grid-cols-[1.4fr_1fr]" data-testid="signature-panel">
          <div className="h-[380px] overflow-hidden md:h-[560px]"><img src={active.img} alt={active.tab} className="h-full w-full object-cover" data-testid="signature-image" /></div>
          <div className="flex flex-col justify-center p-10 md:p-16">
            <p className="kicker">{active.tab}</p>
            <h3 className="font-display mt-4 text-4xl font-light text-neutral-950 md:text-5xl">{active.title}</h3>
            <p className="mt-6 text-base font-light leading-relaxed text-neutral-600">{active.text}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="btn-ink" data-testid="signature-book-btn">Book Now</a>
              <Link to={`/treatments/${active.page}`} className="link-under self-center" data-testid="signature-menu-link">Details & Pricing</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Financing = () => (
  <section id="financing" className="bg-neutral-950 py-24 text-white md:py-32" data-testid="financing-section">
    <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-4 md:px-8 lg:grid-cols-2">
      <Reveal className="relative h-[440px]">
        <img src={IMG.financingArt} alt="Private treatment room" className="absolute left-0 top-0 h-[80%] w-[70%] object-cover" loading="lazy" />
        <img src={IMG.products} alt="Medical-grade skincare" className="absolute bottom-0 right-0 h-[55%] w-[48%] border-[10px] border-neutral-950 object-cover grayscale" loading="lazy" />
      </Reveal>
      <Reveal>
        <p className="text-[11px] uppercase tracking-[0.3em] text-neutral-400">Flexible Financing with Cherry</p>
        <h2 className="font-display mt-4 text-5xl font-light leading-[1.05] md:text-6xl">Invest in your skin,<br /><em>on your terms.</em></h2>
        <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-neutral-300">
          Comprehensive treatment plans and long-term maintenance can be paid over time with Cherry — a simple application, flexible monthly payments, and no change to the care you receive.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <a href={CHERRY_URL} target="_blank" rel="noreferrer" className="btn-ghost-light" data-testid="cherry-apply-btn">Apply with Cherry</a>
          <Link to="/contact" className="inline-flex items-center px-2 text-[11px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:text-white" data-testid="cherry-ask-link">Ask About Plans</Link>
        </div>
      </Reveal>
    </div>
  </section>
);
